import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RouteEnum } from "../../../common/enums/RouteEnum";
import {
    useExportRefurbishedProductMutation,
    useGetRefurbishedProductDetailQuery,
} from "../../../common/api/refurbished";
import { useProductDetailSession } from "../../../common/hooks/productDetail/useProductDetailSession";
import type { UseProductDetailSessionResult } from "../../../common/hooks/productDetail/useProductDetailSession";
import { isProductDetailPageLoading } from "../../../common/utils/productDetail/isProductDetailPageLoading";
import { useCart } from "../../cartPage/hooks/useCart";
import { normalizeRefurbishedState } from "../../../common/utils/normalizeRefurbishedState";
import {
    getRefurbishedProductButtonVariant,
    isReservedByCurrentUser,
} from "../../refurbishedPage/utils/getRefurbishedProductButton";
import { canReserveProduct } from "../../cartPage/utils/canReserveProduct";
import { getRefurbishedPricing } from "../../../common/utils/getRefurbishedPricing";
import {
    flattenProductCategoryLabels,
    RefurbishedProductDetailInterface,
} from "../../../common/interfaces/apiV2/refurbished/RefurbishedProductDetailInterface";
import { createMailtoUrl } from "../../../common/utils/productInforBarTransformer";
import { downloadBlob } from "../../../common/utils/Export";
import { LanguageInterface } from "../../../common/interfaces/shared/LanguageInterface";
import type { ProductCardButtonVariant } from "@hatch-digital-nl/bravilor-storybook";
import type { UseCartReturn } from "../../cartPage/hooks/useCart";
import { TranslationsInterface } from "../../../common/interfaces/shared/TranslationsInterface";

interface UseRefurbishedProductDetailReturn {
    refurbishedProductId: string | undefined;
    data: RefurbishedProductDetailInterface | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isPageLoading: boolean;
    translationsLoaded: boolean;
    activeLanguage: LanguageInterface | null;
    translations: TranslationsInterface | undefined;
    userData: UseProductDetailSessionResult["userData"];
    productName: string;
    pricing: { current: string; original: string | undefined };
    categoryBreadcrumbs: { label: string }[];
    reserveButtonVariant: ProductCardButtonVariant;
    reserveButtonLabel: string;
    canReserve: boolean;
    isOwnReservation: boolean;
    isDownloading: boolean;
    isReservingProduct: UseCartReturn["isReservingProduct"];
    labels: {
        identifier: string;
        serialNumber: string;
        requestButton: string;
        downloadButton: string;
        refurbished: string;
        priceTooltipTitle: string;
        priceTooltipSubtitle: string;
    };
    reserveProductById: UseCartReturn["reserveProductById"];
    handleDownload: (
        productData: RefurbishedProductDetailInterface,
    ) => Promise<void>;
    createMailtoUrl: typeof createMailtoUrl;
}

export const useRefurbishedProductDetail =
    (): UseRefurbishedProductDetailReturn => {
        const navigate = useNavigate();
        const { refurbishedProductId } = useParams<{
            refurbishedProductId: string;
        }>();
        const {
            t,
            translations,
            translationsLoaded,
            activeLanguage,
            userData,
            isUserPending,
        } = useProductDetailSession();
        const [isDownloading, setIsDownloading] = useState(false);
        const [exportRefurbishedProduct] =
            useExportRefurbishedProductMutation();

        const shouldFetch = !!refurbishedProductId && !!activeLanguage;

        const {
            data: response,
            isLoading,
            isFetching,
            isError,
            error,
        } = useGetRefurbishedProductDetailQuery(
            {
                id: refurbishedProductId ?? "",
                language: activeLanguage,
            },
            {
                skip: !shouldFetch,
            },
        );

        useEffect(() => {
            if (!shouldFetch || isLoading || isFetching) {
                return;
            }

            const isHttpError =
                isError &&
                error !== undefined &&
                typeof error === "object" &&
                error !== null &&
                "status" in error;

            if (isHttpError) {
                navigate(RouteEnum.REFURBISHED, { replace: true });
            }
        }, [shouldFetch, isLoading, isFetching, isError, error, navigate]);

        const data = response?.data;
        const isProductPending = !shouldFetch || isLoading || isFetching;
        const isPageLoading = isProductDetailPageLoading({
            isUserLoading: isUserPending,
            translationsLoaded,
            activeLanguage,
            isProductPending,
        });
        const {
            pendingOrder,
            reserveProductById,
            isReservingProduct,
            timerDisplay,
        } = useCart();

        const productName = useMemo(() => data?.label ?? "", [data]);

        const normalizedState = useMemo(
            () => (data ? normalizeRefurbishedState(data.state) : null),
            [data],
        );

        const reserveButtonVariant: ProductCardButtonVariant = useMemo(() => {
            if (!data || !normalizedState) {
                return "primary";
            }

            return getRefurbishedProductButtonVariant(
                normalizedState,
                pendingOrder,
                data.refurbished_product_id,
            );
        }, [data, normalizedState, pendingOrder]);

        const isOwnReservation = useMemo(
            () =>
                data
                    ? isReservedByCurrentUser(
                          data.refurbished_product_id,
                          pendingOrder,
                      )
                    : false,
            [data, pendingOrder],
        );

        const canReserve = useMemo(
            () =>
                normalizedState
                    ? canReserveProduct(normalizedState) && !isOwnReservation
                    : false,
            [normalizedState, isOwnReservation],
        );

        const pricing = useMemo(() => {
            if (!data) {
                return {
                    current: "",
                    original: undefined as string | undefined,
                };
            }

            return getRefurbishedPricing(
                data.discounted_price,
                data.akeneo_price,
            );
        }, [data]);

        const categoryBreadcrumbs = useMemo(() => {
            if (!data?.categories?.length) {
                return [];
            }

            return flattenProductCategoryLabels(data.categories).map(
                (label) => ({ label }),
            );
        }, [data]);

        const handleDownload = async (
            productData: RefurbishedProductDetailInterface,
        ): Promise<void> => {
            setIsDownloading(true);

            try {
                const { blob, contentType } = await exportRefurbishedProduct(
                    productData.refurbished_product_id,
                ).unwrap();

                downloadBlob({
                    blob,
                    contentType,
                    fileName: productName || productData.identifier,
                });
            } finally {
                setIsDownloading(false);
            }
        };

        const labels = useMemo(
            () => ({
                identifier: t("art-nr"),
                serialNumber: t("serial-number"),
                requestButton: t("request-information"),
                downloadButton: t("download-productsheet"),
                refurbished: t("refurbished"),
                priceTooltipTitle: t("price-explanation"),
                priceTooltipSubtitle: t("price-explanation-description"),
            }),
            [t],
        );

        const reserveButtonLabel = useMemo(() => {
            if (reserveButtonVariant === "reserved") {
                return t("refurbished-reserved").replace(
                    "{timer}",
                    timerDisplay,
                );
            }

            if (reserveButtonVariant === "reserved-other") {
                return t("refurbished-currently-reserved");
            }

            return t("reserve-machine");
        }, [reserveButtonVariant, t, timerDisplay]);

        return {
            refurbishedProductId,
            data,
            isLoading,
            isFetching,
            isError,
            isPageLoading,
            translationsLoaded,
            activeLanguage,
            translations,
            userData,
            productName,
            pricing,
            categoryBreadcrumbs,
            reserveButtonVariant,
            reserveButtonLabel,
            canReserve,
            isOwnReservation,
            isDownloading,
            isReservingProduct,
            labels,
            reserveProductById,
            handleDownload,
            createMailtoUrl,
        };
    };
